import {
  ActivityIcon,
  BookOpenIcon,
  BoxesIcon,
  BrainIcon,
  ChartBarIcon,
  CompassIcon,
  FileIcon,
  FileTextIcon,
  HeartHandshakeIcon,
  HeartIcon,
  HomeIcon,
  InfoIcon,
  LayoutDashboardIcon,
  type LucideIcon,
  MailIcon,
  MapIcon,
  PackageIcon,
  SettingsIcon,
  ShieldIcon,
  SparklesIcon,
  UsersIcon
} from 'lucide-react';

/** A leaf link nested under a parent nav item. */
export type NavChild = {
  title: string;
  to: string;
};

/** Primary microsite navigation entry. */
export type NavItem = {
  title: string;
  icon: LucideIcon;
  to?: string;
  href?: string;
  external?: boolean;
  children?: NavChild[];
  /** Optional group label — items sharing the same label are visually
   * bracketed together in the nav bar with a shared identifier. */
  group?: string;
};

export type NavSection = {
  heading: string;
  items: NavItem[];
};

/** Persistent primary header navigation (Build Spec §2). */
export const primaryNavigation: NavItem[] = [
  { title: 'Home', icon: HomeIcon, to: '/' },
  { title: 'NextOnVera', icon: BrainIcon, to: '/nextonvera', group: 'Tech Initiatives' },
  { title: 'Cancer Navigator', icon: CompassIcon, to: '/cancer-navigator', group: 'Tech Initiatives' },
  { title: 'Grace AI', icon: SparklesIcon, to: '/grace-ai', group: 'Tech Initiatives' },
  { title: 'Mark’s Story', icon: BookOpenIcon, to: '/my-story' },
  { title: 'The Fund', icon: HeartHandshakeIcon, to: '/the-fund' },
  { title: 'Why TDC and NextOnVera', icon: ShieldIcon, to: '/why-tdc-nextonvera' }
];

/** Utility / secondary links (footer + overflow). */
export const utilityNavigation: NavItem[] = [
  { title: 'Get Involved', icon: UsersIcon, to: '/get-involved' },
  { title: 'Donate to NextOnVera', icon: HeartIcon, to: '/donate' },
  { title: 'About', icon: InfoIcon, to: '/about' },
  { title: 'Contact', icon: MailIcon, to: '/contact' }
];

/** Alias used by residual template surfaces that still import topNavigation. */
export const topNavigation: NavItem[] = primaryNavigation;

/** Sidebar sections retained for any legacy template pages still in tree. */
export const mainNavigation: NavSection[] = [
  {
    heading: 'NextOnVera',
    items: primaryNavigation
  },
  {
    heading: 'Engage',
    items: utilityNavigation
  }
];

export const adminNavigation: NavSection = {
  heading: 'Administration',
  items: []
};

export type Breadcrumb = {
  section: string;
  item: string;
  child?: string;
};

const allSections: NavSection[] = [...mainNavigation, adminNavigation];

export function findBreadcrumb(pathname: string): Breadcrumb | undefined {
  for (const section of allSections) {
    for (const item of section.items) {
      if (item.to === pathname) {
        return { section: section.heading, item: item.title };
      }
      const child = item.children?.find((entry) => entry.to === pathname);
      if (child) {
        return { section: section.heading, item: item.title, child: child.title };
      }
      if (item.to && item.to !== '/' && pathname.startsWith(`${item.to}/`)) {
        return { section: section.heading, item: item.title };
      }
    }
  }
  return undefined;
}

export {
  ActivityIcon,
  BookOpenIcon,
  BoxesIcon,
  BrainIcon,
  ChartBarIcon,
  CompassIcon,
  FileIcon,
  FileTextIcon,
  HeartHandshakeIcon,
  HeartIcon,
  HomeIcon,
  InfoIcon,
  LayoutDashboardIcon,
  MailIcon,
  MapIcon,
  PackageIcon,
  SettingsIcon,
  ShieldIcon,
  SparklesIcon,
  UsersIcon
};
